import { Ionicons } from '@expo/vector-icons';
import { Redirect, router, useLocalSearchParams } from 'expo-router';
import React, { useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';

import { AddProfessionalSheet } from '@/components/team/add-professional-sheet';
import { MemberAvatar } from '@/components/team/member-avatar';
import { MemberStatusPill } from '@/components/team/member-status-pill';
import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { Card, PressableCard } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { FadeInView } from '@/components/ui/fade-in-view';
import { PageLayout } from '@/components/ui/page-layout';
import { PressableScale } from '@/components/ui/pressable-scale';
import { SkeletonCard } from '@/components/ui/skeleton';
import { useMembership } from '@/hooks/use-membership';
import { usePlanUsage } from '@/hooks/use-provider';
import { useOrganization, useTeamMembers } from '@/hooks/use-team';
import { usePlanLimitStore } from '@/store/planLimitStore';
import { useTerms } from '@/hooks/use-terms';
import { navigateToSubscriptionPlans } from '@/lib/auth/subscription-plans-url';
import { TeamMember } from '@/types/organization';

/** Dono primeiro, depois a ordem que ele escolheu; desativados no fim. */
function sortMembers(members: TeamMember[]): TeamMember[] {
  const rank = (m: TeamMember) => (m.role === 'OWNER' ? 0 : m.status === 'DISABLED' ? 2 : 1);
  return [...members].sort(
    (a, b) => rank(a) - rank(b) || a.displayOrder - b.displayOrder || a.name.localeCompare(b.name),
  );
}

/**
 * Equipe: quem está na organização, quantos assentos o plano cobre e a porta
 * para adicionar alguém. Organização solo (link antigo, deep link) volta para
 * o Perfil, onde fica o "Montar equipe".
 */
export default function TeamScreen() {
  const terms = useTerms();
  const { membership, can } = useMembership();
  const { add } = useLocalSearchParams<{ add?: string }>();
  const [adding, setAdding] = useState(false);

  const allowed = can('team');
  const { data: organization } = useOrganization({ enabled: allowed });
  const { data: members, isLoading, error, refetch } = useTeamMembers({ enabled: allowed });
  const { data: usage } = usePlanUsage();

  const sorted = useMemo(() => sortMembers(members ?? []), [members]);
  const seatsUsed = sorted.filter((m) => m.status === 'ACTIVE' && m.attends).length;
  const contracted = organization?.contractedProfessionals ?? null;

  // Plano cheio: o aviso de assento abre no clique, antes de preencher o
  // formulário. O servidor segue barrando no submit.
  const openAdd = () => {
    if (contracted != null && seatsUsed >= contracted) {
      usePlanLimitStore.setState({
        payload: {
          code: 'PLAN_SEATS_EXCEEDED',
          message: `Seu plano cobre ${contracted} ${terms.professionalPlural}.`,
          requested: seatsUsed + 1,
          limit: contracted,
        },
      });
      return;
    }
    setAdding(true);
  };

  useEffect(() => {
    if (add === '1' && members) openAdd();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [add, members]);

  if (membership && !allowed) {
    return <Redirect href={'/(tabs)/(provider)/profile' as never} />;
  }

  const onlyOwner = sorted.every((m) => m.role === 'OWNER');
  const seatsLabel = contracted
    ? `${seatsUsed} de ${contracted} ${terms.professionalPlural}`
    : `${seatsUsed} ${seatsUsed === 1 ? terms.professional : terms.professionalPlural}`;

  return (
    <>
      <PageLayout
        title="Equipe"
        subtitle={membership?.organizationName}
        buttonTitle={`Adicionar ${terms.professional}`}
        onButtonPress={openAdd}
        headerAction={
          <PressableScale
            haptic
            onPress={() => router.push('/(tabs)/(provider)/team/settings' as never)}
            accessibilityRole="button"
            accessibilityLabel="Configurações da equipe"
            className="h-10 w-10 items-center justify-center rounded-full bg-card"
            style={{ boxShadow: '0 1px 2px rgba(23, 17, 10, 0.06)' }}
          >
            <Ionicons className="text-neutral-900" name="settings-outline" size={19} />
          </PressableScale>
        }
      >
        <Card>
          <View className="flex-row items-center gap-3 p-4">
            <View className="h-10 w-10 items-center justify-center rounded-xl bg-primary/10 dark:bg-primary">
              <Ionicons className="text-primary dark:text-white" name="people-outline" size={20} />
            </View>
            <View className="min-w-0 flex-1">
              <ThemedText
                className="font-semibold text-[15px] text-foreground"
                style={{ fontVariant: ['tabular-nums'] }}
              >
                {seatsLabel}
              </ThemedText>
              {usage?.planName ? (
                <ThemedText className="text-foreground/60 text-sm">
                  plano {usage.planName}
                </ThemedText>
              ) : null}
            </View>
            <Button
              size="sm"
              variant="ghost"
              title="Ajustar plano"
              onPress={() =>
                void navigateToSubscriptionPlans({ push: (path) => router.push(path as never) })
              }
            />
          </View>
        </Card>

        {isLoading ? (
          <View className="gap-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <SkeletonCard key={index} showHeader lines={1} />
            ))}
          </View>
        ) : error ? (
          <ErrorState
            variant="inline"
            title="Erro ao carregar a equipe"
            message="Não foi possível carregar a equipe. Tente novamente."
            onRetry={refetch}
          />
        ) : onlyOwner ? (
          <EmptyState
            icon="people-outline"
            title="Só você por enquanto"
            actionLabel={`Adicionar ${terms.professional}`}
            onAction={openAdd}
            className="py-12"
          />
        ) : null}

        {!isLoading && !error
          ? sorted.map((member, index) => (
              <FadeInView key={member.id} delay={index * 50}>
                <MemberCard member={member} notAttendingLabel="Não atende" />
              </FadeInView>
            ))
          : null}
      </PageLayout>

      <AddProfessionalSheet open={adding} onClose={() => setAdding(false)} />
    </>
  );
}

function MemberCard({
  member,
  notAttendingLabel,
}: {
  member: TeamMember;
  notAttendingLabel: string;
}) {
  const isOwner = member.role === 'OWNER';
  return (
    <PressableCard
      haptic
      onPress={() => router.push(`/(tabs)/(provider)/team/${member.id}` as never)}
      accessibilityRole="button"
      accessibilityLabel={member.name}
      className={member.status === 'DISABLED' ? 'opacity-60' : undefined}
    >
      <View className="flex-row items-center gap-3 p-4">
        <MemberAvatar name={member.name} image={member.image} color={member.color} size={44} />
        <View className="min-w-0 flex-1 gap-1">
          <View className="flex-row items-center gap-2">
            {member.color ? (
              <View className="h-2 w-2 rounded-full" style={{ backgroundColor: member.color }} />
            ) : null}
            <ThemedText
              numberOfLines={1}
              className="shrink font-semibold text-[15px] text-neutral-900"
            >
              {member.name}
            </ThemedText>
            {isOwner ? <ThemedText className="text-sm text-neutral-500">· você</ThemedText> : null}
          </View>
          <View className="flex-row flex-wrap items-center gap-2">
            <MemberStatusPill member={member} />
            {!member.attends ? (
              <ThemedText className="text-xs text-neutral-500">{notAttendingLabel}</ThemedText>
            ) : null}
          </View>
        </View>
        <Ionicons className="text-neutral-400" name="chevron-forward" size={18} />
      </View>
    </PressableCard>
  );
}
